import {
  IAssignee,
  IReceivedDirectMessage,
  IRequiresAttention,
  ISelectorOption,
  IToDo,
} from 'providence-types';
import { createReducer, on } from '@core/utils/store';
import { ERROR_MASK } from '@models/constants';
import {
  AccountingNotificationData,
  DashboardItem,
  DashboardUpdatableItem,
  MultiSetCompletedDirectMessage,
  MultiUpdateAssignees,
  PayloadAndState,
  SetCompletedDirectMessage,
} from '@models/interfaces';
import { DashboardActions } from '../actions/dashboard.actions';

export interface DashboardState {
  adminList: ISelectorOption<string>[];
  userList: ISelectorOption<string>[];
  accountingItems: DashboardItem<AccountingNotificationData>[];
  isAccountingItemsFetching: boolean;
  requiresAttentionItems: DashboardItem<IRequiresAttention>[];
  isRequiresAttentionItemsFetching: boolean;
  toDoItems: DashboardItem<IToDo>[];
  isToDoItemsFetching: boolean;
  directMessages: DashboardItem<IReceivedDirectMessage>[];
  isDirectMessagesFetching: boolean;
  isFetching: boolean;
  errors: boolean;
}

const initialState: DashboardState = {
  adminList: [],
  userList: [],
  accountingItems: [],
  isAccountingItemsFetching: false,
  requiresAttentionItems: [],
  isRequiresAttentionItemsFetching: false,
  toDoItems: [],
  isToDoItemsFetching: false,
  directMessages: [],
  isDirectMessagesFetching: false,
  isFetching: false,
  errors: false,
};

const dashboardItemUpdate = <T extends IToDo | IRequiresAttention>(
  stateData: DashboardItem<T>[],
  { _id, assignees, taskNote, reporter }: DashboardUpdatableItem,
): DashboardItem<T>[] =>
  stateData.map((dashboardItem: DashboardItem<T>) =>
    dashboardItem._id === _id
      ? {
          ...dashboardItem,
          notificationData: {
            ...dashboardItem.notificationData,
            assignees: assignees.map((assignee: string) => ({
              reporter,
              assignee,
            })),
            taskNote,
          },
        }
      : dashboardItem,
  );

const multiSetAssignees = <T extends IToDo | IRequiresAttention>(
  stateData: DashboardItem<T>[],
  { recordIds, assignees, reporter }: MultiUpdateAssignees,
): DashboardItem<T>[] =>
  stateData.map((dashboardItem: DashboardItem<T>) =>
    recordIds.includes(dashboardItem._id)
      ? {
          ...dashboardItem,
          notificationData: {
            ...dashboardItem.notificationData,
            assignees: [
              ...assignees.reduce(
                (acc: IAssignee[], newAssignee: string) =>
                  dashboardItem.notificationData.assignees.find(
                    ({ assignee }: IAssignee) => assignee === newAssignee,
                  )
                    ? acc
                    : [...acc, { assignee: newAssignee, reporter }],
                [...dashboardItem.notificationData.assignees],
              ),
            ],
          },
        }
      : dashboardItem,
  );

export const reducer = createReducer(
  initialState,
  on(
    DashboardActions.getAdminList,
    ({
      payload: adminList,
    }: PayloadAndState<ISelectorOption<string>[], DashboardState>) => ({
      adminList,
    }),
  ),
  on(
    DashboardActions.getUserList,
    ({
      payload: userList,
    }: PayloadAndState<ISelectorOption<string>[], DashboardState>) => ({
      userList,
    }),
  ),
  // get ToDoItems
  on(
    DashboardActions.getAssignedToDoItems,
    ({
      payload: toDoItems,
    }: PayloadAndState<DashboardItem<IToDo>[], DashboardState>) => ({
      toDoItems,
    }),
    { ...ERROR_MASK, fetchingMask: 'isToDoItemsFetching' },
  ),
  on(
    DashboardActions.getAllToDoItems,
    ({
      payload: toDoItems,
    }: PayloadAndState<DashboardItem<IToDo>[], DashboardState>) => ({
      toDoItems,
    }),
    { ...ERROR_MASK, fetchingMask: 'isToDoItemsFetching' },
  ),
  // update ToDoItem
  on(
    DashboardActions.updateToDoItem,
    ({
      payload,
      state: { toDoItems },
    }: PayloadAndState<DashboardUpdatableItem, DashboardState>) => ({
      toDoItems: [...dashboardItemUpdate(toDoItems, payload)],
    }),
  ),
  // multi update ToDoItem assignees
  on(
    DashboardActions.multiUpdateToDoAssignees,
    ({
      payload,
      state: { toDoItems },
    }: PayloadAndState<MultiUpdateAssignees, DashboardState>) => ({
      toDoItems: [...multiSetAssignees(toDoItems, payload)],
    }),
  ),
  // get RequiresAttentionItems
  on(
    DashboardActions.getAssignedRequiresAttentionItems,
    ({
      payload: requiresAttentionItems,
    }: PayloadAndState<
      DashboardItem<IRequiresAttention>[],
      DashboardState
    >) => ({
      requiresAttentionItems,
    }),
    { ...ERROR_MASK, fetchingMask: 'isRequiresAttentionItemsFetching' },
  ),
  on(
    DashboardActions.getAllRequiresAttentionItems,
    ({
      payload: requiresAttentionItems,
    }: PayloadAndState<
      DashboardItem<IRequiresAttention>[],
      DashboardState
    >) => ({
      requiresAttentionItems,
    }),
    { ...ERROR_MASK, fetchingMask: 'isRequiresAttentionItemsFetching' },
  ),
  // update RequiresAttentionItem
  on(
    DashboardActions.updateRequiresAttentionItem,
    ({
      payload,
      state: { requiresAttentionItems },
    }: PayloadAndState<DashboardUpdatableItem, DashboardState>) => ({
      requiresAttentionItems: [
        ...dashboardItemUpdate(requiresAttentionItems, payload),
      ],
    }),
  ),
  // multi update RequiresAttentionItem assignees
  on(
    DashboardActions.multiUpdateRequiresAttentionAssignees,
    ({
      payload,
      state: { requiresAttentionItems },
    }: PayloadAndState<MultiUpdateAssignees, DashboardState>) => ({
      requiresAttentionItems: [
        ...multiSetAssignees(requiresAttentionItems, payload),
      ],
    }),
  ),
  // get AccountingItems
  on(
    DashboardActions.getAccountingItems,
    ({
      payload: accountingItems,
    }: PayloadAndState<
      DashboardItem<AccountingNotificationData>[],
      DashboardState
    >) => ({
      accountingItems,
    }),
    { ...ERROR_MASK, fetchingMask: 'isAccountingItemsFetching' },
  ),
  // get ReceivedDirectMessage
  on(
    DashboardActions.getDirectMessage,
    ({
      payload: directMessages,
    }: PayloadAndState<
      DashboardItem<IReceivedDirectMessage>[],
      DashboardState
    >) => ({
      directMessages,
    }),
    { ...ERROR_MASK, fetchingMask: 'isDirectMessagesFetching' },
  ),
  on(
    DashboardActions.setCompletedDirectMessage,
    ({
      payload: { _id, completed },
      state: { directMessages },
    }: PayloadAndState<SetCompletedDirectMessage, DashboardState>) => ({
      directMessages: completed
        ? directMessages.map(
            (directMessage: DashboardItem<IReceivedDirectMessage>) => ({
              ...directMessage,
              notificationData: {
                ...directMessage.notificationData,
                completed:
                  directMessage._id === _id
                    ? true
                    : directMessage.notificationData.completed,
              },
            }),
          )
        : directMessages.filter(
            (directMessage: DashboardItem<IReceivedDirectMessage>) =>
              directMessage._id !== _id,
          ),
    }),
  ),
  on(
    DashboardActions.multiSetCompletedDirectMessage,
    ({
      payload: { recordIds, completed },
      state: { directMessages },
    }: PayloadAndState<MultiSetCompletedDirectMessage, DashboardState>) => ({
      directMessages: completed
        ? directMessages.map(
            (directMessage: DashboardItem<IReceivedDirectMessage>) => ({
              ...directMessage,
              notificationData: {
                ...directMessage.notificationData,
                completed: recordIds.includes(directMessage._id)
                  ? true
                  : directMessage.notificationData.completed,
              },
            }),
          )
        : directMessages.filter(
            (directMessage: DashboardItem<IReceivedDirectMessage>) =>
              !recordIds.includes(directMessage._id),
          ),
    }),
  ),
);
